function mockInPlace(obj, takeFromObj) {
  for (const key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    if (typeof obj[key] === "function") {
      obj[key] = takeFromObj[key];
    } else if (typeof obj[key] === "object") {
      mockInPlace(obj[key], takeFromObj[key]);
    }
  }
}
/**
 * Checks two objects for deep equality
 */
export function deepEqual(a, b) {
  if (typeof a !== typeof b) return false;
  if (
    typeof a === "string" ||
    typeof a === "number" ||
    typeof a === "symbol" ||
    typeof a === "function" ||
    typeof a === "boolean" ||
    typeof a === "undefined"
  )
    return a === b;
  if (typeof a === "object") {
    if (a === null || b === null) return a === b;
    const aKeys = new Set();
    for (const key in a) {
      if (!a.hasOwnProperty(key)) continue;
      const ret = deepEqual(a[key], b[key]);
      if (!ret) return false;
      aKeys.add(key);
    }
    for (const key in b) {
      if (aKeys.has(key)) continue; // already compared
      if (!b.hasOwnProperty(key)) continue;
      const ret = deepEqual(a[key], b[key]);
      if (!ret) return false;
    }
  }
  return true;
}
const defaultOptions = {
  autoRespond: true,
  strict: true,
};
/**
 * Mock a client and return a "server"
 *
 * This function takes an API client object and recursively looks for properties
 * that are functions. It then mutates the provided client object, converting
 * function properties to stubbed functions. It returns a "server", which has an
 * identical interface to the client, except the function properties control the
 * client's stubs.
 */
export function mockClient(client, options = null) {
  let name = !options || typeof options === "string" ? options : options.name;
  if (!name) {
    if (client.NAME) name = client.NAME;
    else {
      // tslint:disable-next-line:no-console
      console.warn(
        `mockClient(client, clientName) called without a \`clientName\` and \`client.NAME\` is unset.\n` +
          `You should either call mockClient with a clientName, or add a \`NAME\` property to your ` +
          `client. This will make debugging output more clear.`
      );
      name = "[missing]";
    }
  }
  const mockClientAndServer = new MockClientAndServer(client, name, options);
  mockInPlace(client, mockClientAndServer.client);
  return mockClientAndServer.server;
}
/** @hidden */
export class MockClientAndServer {
  constructor(client, clientName, options) {
    this.mockedMethods = [];
    this.clientName = clientName;
    this.server = {};
    this.client = {};
    this.options = {
      ...defaultOptions,
      ...(options || {}),
    };
    this.mockObject(clientName, "", client);
    const methodNames = ["debug", "reset", "verify"];
    for (const methodName of methodNames) {
      if (this.server[methodName])
        throw new Error(
          `The client has a \`${methodName}\` method, which conflicts with a built-in feature of the mock server. Maybe use a different name for that method!`
        );
    }
    let verified = false;
    let firstRun = true;
    this.server.debug = (pad = 0) => {
      return this.mockedMethods
        .map(method => method.log(pad))
        .filter(log => !!log)
        .join("\n");
    };
    this.server.reset = () => {
      if (!verified && !firstRun) {
        // tslint:disable-next-line:no-console
        console.warn(
          `You must call ${clientName}.verify() between calls to ${clientName}.reset()`
        );
      }
      this.mockedMethods.forEach(m => m.reset());
      firstRun = false;
      verified = false;
    };
    this.server.verify = () => {
      verified = true;
      if (this.options.strict) {
        for (const mockedMethod of this.mockedMethods) {
          mockedMethod.maybeThrowRequestError();
        }
      }
      const unsentResponses = [].concat(
        ...this.mockedMethods.map(m => m.getUnsentResponses())
      );
      if (unsentResponses.length) {
        throw new Error(
          "Some mocked methods were called but no response was ever sent:\n" +
            unsentResponses.map(u => `  ${u.getSignature()}`).join("\n")
        );
      }
    };
  }
  mockObject(clientName, path, obj) {
    const pathArr = path === "" ? [] : path.split(".");
    for (const key in obj) {
      if (!obj.hasOwnProperty(key)) continue;
      if (typeof obj[key] === "function") {
        const mock = this.makeMock([...pathArr, key].join("."));
        let thisServerNode = this.server;
        let thisClientNode = this.client;
        for (const p of pathArr) {
          thisServerNode[p] = thisServerNode[p] || {};
          thisClientNode[p] = thisClientNode[p] || {};
          thisServerNode = thisServerNode[p];
          thisClientNode = thisClientNode[p];
        }
        thisServerNode[key] = mock.serverImplementation;
        thisClientNode[key] = mock.clientImplementation;
        this.mockedMethods.push(mock);
      } else if (typeof obj[key] === "object") {
        this.mockObject(clientName, [...pathArr, key].join("."), obj[key]);
      }
    }
  }
  makeMock(path) {
    return new MethodClientAndServer(this, path, this.options);
  }
}
class MethodClientAndServer {
  constructor(mockClientAndServer, path, options) {
    this.registered = [];
    this.requestErrors = [];
    this.clientImplementation = (...args) => {
      // The "client" implementation should be used in the application under test
      // its return values are set up by the "server" implementation.
      let reqres;
      // wanted: findLast
      for (let i = this.registered.length - 1; i >= 0; i--) {
        const isMatch = this.registered[i].match(args);
        if (isMatch) {
          reqres = this.registered[i];
          break;
        }
      }
      if (reqres) {
        return reqres.receiveRequest();
      } else {
        this.requestErrors.push({
          args,
          error: new Error(
            `No response registered that matches ${
              this.mockClientAndServer.clientName
            }.${this.path}(${JSON.stringify(args).slice(
              1,
              -1
            )})\n\n   Current ${
              this.mockClientAndServer.clientName
            } mocks:\n   ${this.mockClientAndServer.server.debug(3)}`
          ),
        });
        return new Promise(_ => {});
      }
    };
    this.serverImplementation = (...args) => {
      // The "server" implementation is available in test cde and is used to
      // change the behavior of the "client" implementation
      const mock = new ControllableMethodMock(args, this);
      this.registered.push(mock);
      return mock;
    };
    this.mockClientAndServer = mockClientAndServer;
    this.path = path;
    this.options = options;
  }
  log(padding = 0) {
    const pad = " ".repeat(padding);
    if (!this.registered.length && !this.requestErrors.length) {
      return null;
    }
    const lines = [
      ...this.registered.map(mockMethod => pad + mockMethod.log(padding)),
      ...this.requestErrors.map(
        requestError =>
          pad +
          `${this.getName()}(${JSON.stringify(requestError.args).slice(
            1,
            -1
          )}) => REQUESTED (response not defined)`
      ),
    ];
    return lines.join("\n");
  }
  reset() {
    this.registered = [];
    this.requestErrors = [];
  }
  getUnsentResponses() {
    return this.registered.filter(r => !r.didRespond(r.getTimes(), true));
  }
  maybeThrowRequestError() {
    if (this.requestErrors.length) {
      throw this.requestErrors.shift().error;
    }
  }
  getName() {
    return `${this.mockClientAndServer.clientName}.${this.path}`;
  }
}
class ControllableMethodMock {
  constructor(matchArgs, method) {
    this.respondImmediately = true;
    this.resolveOrReject = "resolve";
    this.responded = 0;
    this.respondTimes = 1;
    this.deferreds = [];
    this.attemptedResponses = 0;
    if (typeof matchArgs[0] === "function") {
      this.match = matchArgs[0];
      this.matchString = "<function>";
    } else {
      this.match = args => deepEqual(matchArgs, args);
      this.matchString = JSON.stringify(matchArgs).slice(1, -1);
    }
    this.respondImmediately = method.options.autoRespond;
    this.method = method;
  }
  receiveRequest() {
    const {
      registeredResponse,
      defaultResponse,
      resolveOrReject,
      deferreds,
    } = this;
    const hasResponse = typeof registeredResponse !== "undefined";
    const hasDefaultResponse = typeof defaultResponse !== "undefined";
    const respondImmediately = this.respondImmediately || !hasResponse;
    if (!hasResponse && !hasDefaultResponse) {
      throw new Error("No response registered");
    }
    if (this.responded === this.respondTimes)
      throw new Error("Already responded to this request");
    let deferred;
    const promise = new Promise(resolve => {
      deferred = {
        calledResolve: false,
        resolve: function() {
          this.calledResolve = true;
          resolve();
        },
      };
      if (hasResponse) {
        deferreds.push(deferred);
      }
    }).then(() => {
      if (hasResponse) {
        // default responses don't count
        this.responded++;
      }
      const response = hasResponse ? registeredResponse : defaultResponse;
      if (resolveOrReject === "reject") {
        throw response;
      } else {
        return response;
      }
    });
    if (respondImmediately) {
      deferred.resolve();
    }
    return promise;
  }
  getSignature() {
    return `${this.method.getName()}(${this.matchString})`;
  }
  log(padding = 0) {
    const pad = " ".repeat(padding);
    const logEntries = [];
    for (let i = 0; i < this.respondTimes; i++) {
      let status;
      const deferred = this.deferreds[i];
      if (deferred && deferred.calledResolve) {
        status = "RESPONDED";
      } else {
        const statusName = deferred ? "REQUESTED" : "PENDING";
        status = `${statusName} (${this.resolveOrReject})`;
      }
      logEntries.push(`${pad}${this.getSignature()} -> ${status}`);
    }
    return logEntries.join("\n");
  }
  respond() {
    if (this.responded === this.respondTimes)
      throw new Error("Already responded to this request");
    if (this.respondImmediately) {
      throw new Error("This is an auto-responding request");
    }
    this.attemptedResponses++;
    if (
      this.deferreds.length <= this.responded ||
      !this.deferreds.find(d => !d.calledResolve)
    ) {
      this.method.maybeThrowRequestError();
      if (typeof this.registeredResponse === "undefined")
        throw new Error("No response registered");
      throw new Error(
        `Called respond, but no request for ${this.getSignature()} was made`
      );
    }
    for (const deferred of this.deferreds) {
      deferred.resolve();
    }
    return this;
  }
  autoRespondWithDefault(response) {
    this.defaultResponse = response;
    this.respondTimes = 0;
    return this;
  }
  respondWith(response) {
    if (typeof this.registeredResponse !== "undefined") {
      throw new Error("Response already registered");
    }
    if (typeof response === "undefined")
      throw new Error("Cannot register an undefined response");
    if (this.respondTimes === 0) {
      this.respondTimes = 1;
    }
    this.registeredResponse = response;
    return this;
  }
  rejectWith(error) {
    if (typeof this.registeredResponse !== "undefined")
      throw new Error("Response already registered");
    if (typeof error === "undefined")
      throw new Error("Cannot register an undefined response");
    this.registeredResponse = error;
    this.resolveOrReject = "reject";
    return this;
  }
  times(times) {
    if (typeof this.registeredResponse === "undefined") {
      throw new Error(
        "Please register a response before specifying how many times it can be returned"
      );
    } else if (times < this.responded) {
      throw new Error(
        `You called times(${times}), but have already used this response ${this.responded}. This is not allowed.`
      );
    } else if (times < 0) {
      throw new Error("A response cant be returned a negative number of times");
    }
    this.respondTimes = times;
  }
  getTimes() {
    return this.respondTimes;
  }
  dontAutoRespond() {
    this.respondImmediately = false;
    return this;
  }
  autoRespond() {
    this.respondImmediately = true;
    return this;
  }
  didRespond(atLeastTimes = 1, includeAttempted = false) {
    return (
      this.responded + (includeAttempted ? this.attemptedResponses : 0) >=
      atLeastTimes
    );
  }
}
